/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { gnoment, gnomentToCalendarDate, calendarDateToGnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';

import {
    Heading,
    Icon,
    SecurityHelper,
    Select,
    Button,
    Stack,
    Box,
    DividerNew,
    Divider,
    DatePicker,
    localToday,
    withLDConsumer,
    FlexRow,
    FlexCol,
    ProgressBar,
    Text,
    FauxLabel,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { withRecaptchaFallback } from 'shared-ui/recaptcha';

import SingleDatePickerCalendar from '../single-date-picker-calendar';
import RecaptchaChallengeModal from '../RecaptchaChallengeModal';
import VenueFilter from './VenueFilter';
import * as pActions from '../../actions/permit';
import {
    getCurrentTime,
    issuanceStatuses,
    readRuleAsMoment,
    isDivisionOpen,
    addFilterValues,
    hasRule,
} from '../../utils/utilsNew';
import {
    hasAccessToPermit,
    hasAccessToLottery,
    seedQuotaFilters,
    readQuotaFilters,
    filterAvailability,
    getFilterErrors,
    ignoreQuotaAfterFirstDay,
    isDayBlocked,
    isSingleDayStay,
    isWatercraftQuota,
} from '../../utils/availability-utils-new';

import {
    findNextAvailableDay,
    parsePermitSeasonInfo,
} from '../../utils/availability-utils-scratch';
import * as types from '../../constants';
import * as recaptchaConstants from '../../constants/recaptcha';

import { getGateA } from '../../../shared-components/utils/gateA';

export class PermitAvailability extends React.Component {
    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        account: PropTypes.shape({
            access_token: PropTypes.string,
            account: PropTypes.shape({}),
            expiration: PropTypes.string,
        }),
        availabilities: PropTypes.shape({}),
        checkIn: PropTypes.string,
        currentDate: PropTypes.any,
        issuanceError: PropTypes.shape({}),
        permit: PropTypes.shape({
            id: PropTypes.string,
            divisions: PropTypes.shape({}),
            rules: PropTypes.array,
            time_zone: PropTypes.string,
            lotteries: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string,
                    is_daily: PropTypes.bool,
                })
            ),
        }),
        divisionId: PropTypes.string,
        issuance: PropTypes.shape({
            id: PropTypes.string,
        }),
        availability: PropTypes.shape({
            nums: PropTypes.shape({}),
            quota_type_maps: PropTypes.shape({}),
        }),
        handleDivisionSelection: PropTypes.func,
        handleWalkMeThroughClick: PropTypes.func,
        disableCalendar: PropTypes.bool,
        showTheLoginModal: PropTypes.func,
        hasWalkMeThroughButton: PropTypes.bool,
        pActions: PropTypes.object.isRequired,
        aggSeasonRules: PropTypes.array,
        lotteries: PropTypes.array,
        focusErrorNotification: PropTypes.func,
        rightRailOnly: PropTypes.bool,
        hasTitle: PropTypes.bool,
        flags: PropTypes.any,
        recaptcha: PropTypes.object,
    };

    static defaultProps = {
        permit: {},
        hasWalkMeThroughButton: false,
        focusErrorNotification: () => {},
        handleWalkMeThroughClick() {},
    };

    constructor(props) {
        super(props);
        this.state = {
            isFetching: false,
            calendarSelectedDate: null,
            changingDate: false,
            calendarStartDate: null,
            noneAvailable: false,
            quotaFilterValues: {},
            nextLotteryRoute: null,
            nextStartDate: null,
            fakeDivisionSelector: true,
            section: '',
            checkInSet: false,
            isChallengeModalOpen: false,
        };

        // handler methods
        this.handleCalendarClick = this.handleCalendarClick.bind(this);
        this.handleBookNow = this.handleBookNow.bind(this);
        this.handleDetailedAvailabilityClick =
            this.handleDetailedAvailabilityClick.bind(this);
        this.handleNextAvailableClick = this.handleNextAvailableClick.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleWalkMeThroughClick = this.handleWalkMeThroughClick.bind(this);
        this.handleDivisionSelection = this.handleDivisionSelection.bind(this);
        this.handleSectionSelection = this.handleSectionSelection.bind(this);

        this.navigateToPermitRegistration = this.navigateToPermitRegistration.bind(this);
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.account === this.props.account &&
            this.props.account?.account &&
            this.state.nextLotteryRoute &&
            hasAccessToLottery(this.props.account.account, this.props.permit)
        ) {
            this.props.pActions.sendCurrentDate(prevProps.currentDate);
            this.props.router.navigate(this.state.nextLotteryRoute);
        }

        if (
            this.props.account &&
            !prevProps.account &&
            hasAccessToPermit(this.props.account.account, this.props.permit.id)
        ) {
            if (
                this.state.nextLotteryRoute &&
                hasAccessToLottery(this.props.account.account, this.props.permit)
            ) {
                this.props.pActions.sendCurrentDate(this.props.currentDate);
                this.props.router.navigate(this.state.nextLotteryRoute);
            } else if (this.state.nextStartDate) {
                this.navigateToPermitRegistration(this.state.nextStartDate);
            }
        }

        if (this.props.availability) {
            this.setNextAvailable(this.props.availability, prevProps, prevState);
        }

        this.updateCalendarDates(prevProps, prevState);
    }

    // eslint-disable-next-line react/sort-comp
    updateCalendarDates(prevProps, prevState) {
        if (
            !this.state.checkInSet &&
            prevProps.availability !== this.props.availability
        ) {
            const searchDate = gnoment(this.props.checkIn);
            if (searchDate.isValid()) {
                const calendarSelectedDate = searchDate.startOf('day').utc();
                const props = {
                    permit: this.props.permit,
                    blockLotteryDates: true,
                    currentDate: this.props.currentDate,
                    dateMap: this.computeCalendarDateMap(this.props.availability),
                };
                const blocked = isDayBlocked(calendarSelectedDate, props);
                if (!blocked) {
                    // use changingDate hack to fastforward the calendar to the correct month.
                    this.setState(
                        {
                            changingDate: true,
                            calendarSelectedDate,
                            calendarStartDate: calendarSelectedDate,
                        },
                        () => {
                            this.setState({
                                changingDate: false,
                            });
                        }
                    );
                }
            }
            this.setState({
                checkInSet: true,
            });
        }

        if (
            !this.state.calendarSelectedDate &&
            prevState.calendarSelectedDate !== this.state.calendarSelectedDate
        ) {
            // set calendarSelectedDate to searchDate or today iff it is a preliminaryCalendar permit
            // only do this if it hasn't been set yet.

            const currentDate = this.props.currentDate;
            const displayStart = readRuleAsMoment(
                'DisplayDate',
                this.props.aggSeasonRules,
                this.props.permit.time_zone,
                undefined,
                true
            );
            const displayEnd = readRuleAsMoment(
                'SeasonEnd',
                this.props.aggSeasonRules,
                this.props.permit.time_zone
            );

            if (
                displayStart.isValid() &&
                displayEnd.isValid() &&
                currentDate.isBetween(displayStart, displayEnd, 'minute', '[]')
            ) {
                if (
                    get(this.props, 'permit.components.permit_section_filter') ||
                    get(this.props, 'permit.components.reservation_type_question') ||
                    !(
                        ignoreQuotaAfterFirstDay(this.props.availability) ||
                        (isSingleDayStay(this.props.permit) &&
                            isWatercraftQuota(this.props.availability))
                    )
                ) {
                    const searchDate = gnoment(this.props.checkIn);
                    this.setState({
                        calendarSelectedDate: searchDate.isValid()
                            ? searchDate
                            : gnoment(),
                    });
                }
            }
        }
    }

    getDivisionOptions() {
        const divisions = this.props.permit.divisions;
        const divisionArray = Object.keys(divisions).map(
            (divisionId) => divisions[divisionId]
        );
        const keysInOrder = sortBy(divisionArray, 'view_order').map(
            (division) => division.id
        );
        const options = keysInOrder
            .filter(
                (divisionId) =>
                    isDivisionOpen(divisions[divisionId].status) &&
                    !get(divisions, `${divisionId}.is_hidden`, false)
            )
            .map((divisionId) => ({
                value: divisionId,
                label: divisions[divisionId].name,
            }));

        return options;
    }

    getDivisionLabel() {
        let type;
        try {
            const { divisions } = this.props.permit;
            const divisionArray = Object.keys(divisions).map(
                (divisionId) => divisions[divisionId]
            );
            const firstDivision = divisionArray.shift();
            type = firstDivision.type;
        } catch (e) {
            type = 'Division';
        }
        return `Select ${type}`;
    }

    getSectionOptions() {
        const permitSections = get(this.props, 'permit.permit_sections', []);
        const options = permitSections.map((section) => ({
            value: section.name,
            label: section.name,
        }));

        return options;
    }

    setNextAvailable(availability, prevProps, prevState) {
        const { time_zone, rules } = prevProps.permit;
        const blockNonLotteryDates = false;
        const blockLotteryDates = true;
        let firstFoundDate = null;

        const permitSeasonInfo = parsePermitSeasonInfo(
            prevProps.aggSeasonRules,
            time_zone,
            '',
            rules
        );
        if (availability.bools && Object.keys(availability.bools).length > 0) {
            firstFoundDate = findNextAvailableDay(
                availability.bools,
                permitSeasonInfo,
                blockNonLotteryDates,
                blockLotteryDates,
                getCurrentTime(prevProps)
            );
        }

        if (
            prevState.noneAvailable === this.state.noneAvailable &&
            prevState.calendarStartDate === this.state.calendarStartDate
        ) {
            return;
        }

        if (firstFoundDate) {
            this.setState({
                calendarStartDate: firstFoundDate,
                noneAvailable: false,
            });
        } else {
            this.setState({
                noneAvailable: true,
            });
        }
    }

    handleDivisionSelection(event) {
        // We set the division in PermitVenuePage to avoid a null division, so we fake it here the first time.
        this.setState({
            fakeDivisionSelector: false,
        });
        this.props.handleDivisionSelection(event.target.value);
    }

    handleSectionSelection(event) {
        this.setState({ section: event.target.value });
    }

    handleCalendarClick(calendarSelectedDate) {
        this.setState({ calendarSelectedDate });
    }

    handleBookNow() {
        const loggedIn = SecurityHelper.isLoggedIn();
        if (!loggedIn) {
            this.setState({ nextStartDate: this.state.calendarSelectedDate });
            this.props.showTheLoginModal();
            return;
        }
        this.navigateToPermitRegistration(this.state.calendarSelectedDate);
    }

    handleWalkMeThroughClick() {
        this.props.handleWalkMeThroughClick();
    }

    async navigateToPermitRegistration(startDate) {
        this.pendingStartDate = startDate;

        if (
            this.props.recaptcha.isChallengeRequired &&
            !this.state.isChallengeModalOpen
        ) {
            this.setState({ isChallengeModalOpen: true });
            return;
        }

        let item = {
            permit_id: this.props.permit.id,
            division_id: this.props.divisionId,
            user_id: SecurityHelper.getAccount().account_id,
            start_date: startDate,
            status: issuanceStatuses.get(types.PERMIT_PRELIMINARY),
            animals: [],
            pets: [],
        };
        item = Object.assign(item, addFilterValues(this.state.quotaFilterValues));
        this.setState({
            isFetching: true,
        });

        if (!SecurityHelper.isCommercialAccount() && get(item.group_info, 'length')) {
            const { first_name, last_name } = SecurityHelper.getAccount();
            const firstGroupMember = { ...item.group_info[0], first_name, last_name };
            item.group_info[0] = firstGroupMember;
        }

        item.itinerary = [
            {
                start_date: startDate,
                end_date: startDate,
                division_id: this.props.divisionId,
            },
        ];

        const recaptcha = await getGateA(
            this.props.recaptcha,
            recaptchaConstants.CH_PERMIT_VENUE_BOOKING
        );

        this.props.pActions
            .processBooking({ doRedirect: false, item, recaptcha })
            .then(() => {
                this.props.pActions.saveFilters(this.state.quotaFilterValues);
            })
            .then(() => {
                this.props.recaptcha.resetChallenge();
                this.setState({ isChallengeModalOpen: false, isFetching: false });
                if (!this.props.issuanceError) {
                    const reservationId = `${this.props.issuance.id}${types.RES_ID_DELIMITER}${this.props.permit.id}`;
                    const redirectURL = `/permits/${this.props.permit.id}/registration/${reservationId}`;
                    this.props.router.navigate(redirectURL);
                }
            })
            .catch((err) => {
                const { shouldRetry } = this.props.recaptcha.handleRecaptchaError(err);
                if (shouldRetry) {
                    this.setState({ isChallengeModalOpen: true, isFetching: false });
                    return;
                }
                this.setState({ isChallengeModalOpen: false, isFetching: false });
                this.props.focusErrorNotification();
            });
    }

    handleDetailedAvailabilityClick() {
        const { section, calendarSelectedDate } = this.state;

        let url = `/permits/${this.props.permit.id}/registration/detailed-availability`;
        const query = {};
        if (gnoment.isGnoment(calendarSelectedDate)) {
            query.date = `${calendarSelectedDate.format(types.YEAR_MONTH_DAY_DASH_FORMAT)}`;
        }
        if (section) {
            // this is the permit_section_filter
            query.section = section;
        }
        const queryString = Object.keys(query)
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
            .join('&');
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        this.props.router.navigate(url);
    }

    hasMultipleOpenDivisions() {
        // specifically checks if permit has multiple divisions and at least one division has a status of 1 (Open)
        if (
            this.props.permit.divisions &&
            Object.keys(this.props.permit.divisions).length > 1
        ) {
            for (const i in this.props.permit.divisions) {
                if (Object.hasOwn(this.props.permit.divisions, i)) {
                    const division = this.props.permit.divisions[i];
                    if (isDivisionOpen(division.status)) {
                        return true;
                    }
                }
            }
        }
        // false if there is only 1 division, or if there are multiple divisions but none are Open
        return false;
    }

    computeCalendarDateMap(availability) {
        if (!availability) {
            return {};
        }
        return filterAvailability(availability, this.state.quotaFilterValues);
    }

    enterLottery(reservationId, lotteryId) {
        const redirectURL = `/permits/${this.props.permit.id}/lottery/${lotteryId}/registration/${reservationId}`;
        const loggedIn = SecurityHelper.isLoggedIn();
        if (!loggedIn) {
            this.setState({ nextLotteryRoute: redirectURL });
            this.props.showTheLoginModal();
            return;
        }
        this.props.pActions.sendCurrentDate(this.props.currentDate);
        window.location.assign(redirectURL); // could this be a navigate?
    }

    handleNextAvailableClick() {
        if (this.state.calendarStartDate) {
            this.setState(
                {
                    changingDate: true,
                },
                () => {
                    this.setState({
                        changingDate: false,
                    });
                }
            );
        }
    }

    lotteryHasPriority() {
        const currentDate = this.props.currentDate;
        const lotteryStart = readRuleAsMoment(
            'HighUseRegistrationStart',
            this.props.permit.rules,
            this.props.permit.time_zone,
            undefined,
            true
        );
        const lotteryEnd = readRuleAsMoment(
            'HighUseRegistrationEnd',
            this.props.permit.rules,
            this.props.permit.time_zone,
            undefined,
            true
        );

        return (
            lotteryStart.isValid() &&
            lotteryEnd.isValid() &&
            currentDate.isBetween(lotteryStart, lotteryEnd, 'days', '[]')
        );
    }

    isDailyLottery = (lotteryId) => {
        const lotteries = get(this.props, 'permit.lotteries', []);
        return get(
            lotteries.find(({ id }) => id === lotteryId),
            'is_daily',
            false
        );
    };

    handleFilterChange(quotaFilterValues) {
        this.setState({ quotaFilterValues });
    }

    renderSingleDatePickerCalendar() {
        if (this.state.changingDate) {
            return null;
        }

        const { currentDate } = this.props;
        const filterErrorStatement = getFilterErrors(this.state.quotaFilterValues)[0];
        const hasOverlay =
            (this.props.disableCalendar === true && this.hasMultipleOpenDivisions()) ||
            filterErrorStatement;
        return (
            <div className="center-content" inert={hasOverlay ? '' : undefined}>
                <SingleDatePickerCalendar
                    permit={this.props.permit}
                    aggSeasonRules={this.props.aggSeasonRules}
                    divisionId={this.props.divisionId}
                    selectedDate={this.state.calendarSelectedDate}
                    handleDateChange={this.handleCalendarClick}
                    availabilityMap={
                        this.props.availability ? this.props.availability.nums : {}
                    }
                    quotas={get(this.props.availability, 'quota_type_maps', {})}
                    dateMap={this.computeCalendarDateMap(this.props.availability)}
                    initialVisibleMonth={this.state.calendarStartDate || currentDate}
                    currentDate={currentDate}
                    calendarOnly
                    blockLotteryDates
                />
            </div>
        );
    }

    renderProgressBar({ openTime, closeTime, valueLabel }) {
        const totalDuration = closeTime.diff(openTime);
        const lapsedDuration = gnoment().diff(openTime);
        const percentFilled = (lapsedDuration / totalDuration) * 100;

        return (
            <ProgressBar
                label="Days remaining ... "
                value={percentFilled}
                valueLabel={valueLabel}
                size="lg"
                shouldFitContainer
            />
        );
    }

    renderCheckAvailabilityButton() {
        return (
            <Button
                appearance="tertiary"
                onClick={this.handleDetailedAvailabilityClick}
                className="mb-3"
                shouldFitContainer
                screenReaderTextAfter="of the selected Entry Date on the detailed availability page"
            >
                Check Availability
            </Button>
        );
    }

    renderOverlay() {
        if (this.props.disableCalendar === true && this.hasMultipleOpenDivisions()) {
            return (
                <div className="permit-availability-calendar-overlay">
                    <p>Select a location to view availability.</p>
                </div>
            );
        }

        const filterErrorStatement = getFilterErrors(this.state.quotaFilterValues)[0];

        if (filterErrorStatement) {
            return (
                <div className="permit-availability-calendar-overlay">
                    <p>{filterErrorStatement}</p>
                </div>
            );
        }

        return null;
    }

    renderCalendar() {
        const disableBookNow =
            (this.props.disableCalendar && this.hasMultipleOpenDivisions()) ||
            !this.state.calendarSelectedDate ||
            this.state.isFetching;

        const disableNextAvailabile =
            (this.props.disableCalendar && this.hasMultipleOpenDivisions()) ||
            this.state.noneAvailable ||
            !this.state.calendarStartDate;
        let walkMeThroughButton = null;

        if (this.props.hasWalkMeThroughButton) {
            walkMeThroughButton = (
                <Button
                    appearance="tertiary"
                    className="mb-2"
                    onClick={this.handleWalkMeThroughClick}
                    shouldFitContainer
                    screenReaderTextAfter="how to choose a location"
                >
                    Walk me through
                </Button>
            );
        }

        const bookNowText = this.state.isFetching ? 'Loading...' : 'Book Now';
        return (
            <>
                <div className="permit-availability-calendar">
                    {this.renderSingleDatePickerCalendar()}
                    {this.renderOverlay()}
                </div>
                <Button
                    onClick={this.handleBookNow}
                    isDisabled={disableBookNow}
                    shouldFitContainer
                    className="mb-2"
                >
                    {bookNowText}
                </Button>
                {walkMeThroughButton}
                <Button
                    onClick={this.handleDetailedAvailabilityClick}
                    appearance="tertiary"
                    shouldFitContainer
                    screenReaderTextBefore="Navigate to"
                    className="mb-2"
                >
                    Detailed Availability
                </Button>
                <Button
                    appearance="link"
                    className="mb-2"
                    shouldFitContainer
                    onClick={this.handleNextAvailableClick}
                    isDisabled={disableNextAvailabile}
                    screenReaderTextAfter="on the Availability Calendar"
                    iconBeforeElement={<Icon iconName="calendar-note" />}
                >
                    Find Next Available Day
                </Button>
            </>
        );
    }

    renderDivisionSelection() {
        if (this.hasMultipleOpenDivisions()) {
            return (
                <Stack space="sm">
                    <Box className="rec-aside-subtitle">
                        Select an available day to reserve a permit
                    </Box>
                    <Divider />
                    <Select
                        label={this.getDivisionLabel()}
                        id="division-selection"
                        placeholder={`${this.getDivisionLabel()}...`}
                        value={
                            this.state.fakeDivisionSelector ? '' : this.props.divisionId
                        }
                        onChange={this.handleDivisionSelection}
                        options={this.getDivisionOptions()}
                        className="mb-2"
                    />
                </Stack>
            );
        }
        return (
            <Stack space="sm">
                <Box className="rec-aside-subtitle">
                    Select an available day to reserve a permit
                </Box>
                <Divider />
            </Stack>
        );
    }

    renderQuotaFilter() {
        // hide filters when there are no filters or when the only thing you can filter is group size
        const quotaFilters = readQuotaFilters(
            this.props.availabilities,
            this.props.permit,
            SecurityHelper.isCommercialAccount()
        );
        if (
            quotaFilters.length === 0 ||
            (quotaFilters.length === 1 && !quotaFilters[0].showStandalone)
        ) {
            return null;
        }

        const filterValues = seedQuotaFilters(
            this.props.availabilities,
            get(this.props, 'permit'),
            null,
            this.state.quotaFilterValues
        );

        return (
            <VenueFilter
                permit={this.props.permit}
                onFilterChange={this.handleFilterChange}
                quotaFilters={filterValues?.filterValues}
            />
        );
    }

    renderRegistrationIsClosed() {
        let walkMeThroughButton = null;
        if (this.props.hasWalkMeThroughButton) {
            walkMeThroughButton = (
                <div>
                    <p>&nbsp;</p>
                    <Button
                        className="mb-2"
                        appearance="tertiary"
                        shouldFitContainer
                        onClick={this.handleWalkMeThroughClick}
                    >
                        Walk me through
                    </Button>
                </div>
            );
        }

        const importantDates = get(this.props, 'permit.important_dates', []);
        return (
            <div>
                <div className="rec-aside-header">
                    <div>Registration is Closed</div>
                </div>
                <div className="rec-aside-body">
                    <div className="right-rail-card">
                        <p>Reservations have not been released yet.</p>
                        <p>
                            See the important dates section to view when registration
                            opens.
                        </p>
                        <Button
                            id="permit-avail-see-important-dates-btn"
                            appearance="tertiary"
                            onClick={this.props.handleImportantDatesClick}
                            isDisabled={!importantDates.length}
                            shouldFitContainer
                            className="mb-3"
                        >
                            See Important Dates
                        </Button>
                        {walkMeThroughButton}
                    </div>
                </div>
            </div>
        );
    }

    renderSectionSelector() {
        if (!get(this.props, 'permit.components.permit_section_filter')) {
            return null;
        }
        return (
            <div>
                <Select
                    label="Permit Type"
                    id="division-selection"
                    value={this.state.section || ''}
                    onChange={this.handleSectionSelection}
                    options={[...this.getSectionOptions()]}
                />
            </div>
        );
    }

    renderPreliminaryCalendar() {
        return (
            <div>
                {this.renderSectionSelector()}
                <div className="permit-check-availability-calendar">
                    <FauxLabel className="center-content">Entry Date</FauxLabel>
                    <DatePicker
                        numVisibleMonths={1}
                        onChange={(date) =>
                            this.handleCalendarClick(calendarDateToGnoment(date))
                        }
                        value={gnomentToCalendarDate(this.state.calendarSelectedDate)}
                        label="Entry Date"
                        minValue={localToday}
                        showClearDates={false}
                        isLabelVisible={false}
                    />
                </div>
                {this.renderCheckAvailabilityButton()}
            </div>
        );
    }

    renderAvailabilityComponent() {
        const { rightRailOnly, hasTitle } = this.props;
        if (this.props.permit.rules) {
            const ruleLotteryRegistrationOnly = hasRule(
                types.LOTTERY_REGISTRATION_ONLY,
                this.props.permit.rules
            );
            if (ruleLotteryRegistrationOnly) {
                return null;
            }
        }

        const currentDate = this.props.currentDate;
        const displayStart = readRuleAsMoment(
            'DisplayDate',
            this.props.aggSeasonRules,
            this.props.permit.time_zone,
            undefined,
            true
        );
        const displayEnd = readRuleAsMoment(
            'SeasonEnd',
            this.props.aggSeasonRules,
            this.props.permit.time_zone
        );

        if (
            displayStart.isValid() &&
            displayEnd.isValid() &&
            currentDate.isBetween(displayStart, displayEnd, 'minute', '[]')
        ) {
            if (
                !get(this.props, 'permit.components.permit_section_filter') &&
                !get(this.props, 'permit.components.reservation_type_question') &&
                (ignoreQuotaAfterFirstDay(this.props.availability) ||
                    (isSingleDayStay(this.props.permit) &&
                        isWatercraftQuota(this.props.availability)))
            ) {
                return (
                    <div>
                        {(!rightRailOnly || hasTitle) && (
                            <div className="rec-aside-header">
                                <div>
                                    <Heading headingLevel={2} appearance="h4">
                                        Available Permits
                                    </Heading>
                                </div>
                            </div>
                        )}
                        <div className={rightRailOnly ? 'mt-2' : 'rec-aside-body'}>
                            <div className="right-rail-card">
                                {this.renderDivisionSelection()}
                                {this.renderQuotaFilter()}
                                {this.renderCalendar()}
                            </div>
                        </div>
                    </div>
                );
            }
            return (
                <div>
                    <div className="rec-aside-header">
                        <div>
                            <Heading headingLevel={2} appearance="h4">
                                Available Permits
                            </Heading>
                        </div>
                    </div>
                    <div className="rec-aside-body">
                        <div className="right-rail-card">
                            {this.renderPreliminaryCalendar()}
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    renderOpenLotteryComponent() {
        const { lotteries = [], currentDate, permit } = this.props;
        return lotteries
            .map((lottery) => {
                const {
                    id: lotteryId,
                    status,
                    display_at,
                    open_at,
                    close_at,
                    announced_at,
                    inventory_info,
                } = lottery;
                const displayTime = gnoment(display_at).tz(permit.time_zone);
                const openTime = gnoment(open_at).tz(permit.time_zone);
                const closeTime = gnoment(close_at).tz(permit.time_zone);
                const announcedTime = gnoment(announced_at).tz(permit.time_zone);
                const lotteryRangeStart = gnoment(get(inventory_info, 'dates.start')).tz(
                    permit.time_zone
                );
                const lotteryRangeEnd = gnoment(get(inventory_info, 'dates.end')).tz(
                    permit.time_zone
                );

                if (status !== 'LotteryStatusActive') {
                    return null;
                }

                if (
                    !displayTime.isValid() ||
                    !openTime.isValid() ||
                    !closeTime.isValid() ||
                    !lotteryRangeStart.isValid() ||
                    !lotteryRangeEnd.isValid() ||
                    !announcedTime.isValid()
                ) {
                    return null;
                }

                if (!currentDate.isBetween(openTime, closeTime)) {
                    return null;
                }

                if (!hasAccessToLottery(SecurityHelper.getAccount(), permit)) {
                    return this.renderUnauthorizedLotteryComponent();
                }

                let daysLeft = closeTime.diff(currentDate, 'days');
                let daysLeftLabel;
                switch (daysLeft) {
                    case 0:
                        daysLeftLabel = 'day';
                        daysLeft = 'less than 1';
                        break;
                    case 1:
                        daysLeftLabel = 'day';
                        break;
                    default:
                        daysLeftLabel = 'days';
                        break;
                }

                let walkMeThroughButton = null;
                if (this.props.hasWalkMeThroughButton) {
                    walkMeThroughButton = (
                        <Button
                            appearance="tertiary"
                            className="mb-2"
                            shouldFitContainer
                            onClick={this.handleWalkMeThroughClick}
                        >
                            Walk me through
                        </Button>
                    );
                }

                const isDailyLottery = this.isDailyLottery(lottery.id);
                const entryDates = lotteryRangeStart.isSame(lotteryRangeEnd)
                    ? lotteryRangeStart.format(types.DATE_FORMAT)
                    : `${lotteryRangeStart.format(types.DATE_FORMAT)} - ${lotteryRangeEnd.format(types.DATE_FORMAT)}`;

                return (
                    <div key={lotteryId}>
                        <div className="rec-aside-header">
                            <div>
                                {`${isDailyLottery ? 'Daily ' : ''}Lottery `}
                                Open
                            </div>
                        </div>
                        <div className="rec-aside-body">
                            <div className="right-rail-card">
                                <div className="rec-form-item-wrapper-full">
                                    <div className="rec-form-item-wrap-full">
                                        <Text>
                                            This lottery can give you a reservation for
                                            this permit during the following dates:
                                        </Text>
                                        <Text
                                            size="lg"
                                            fontWeight="semibold"
                                            className=""
                                        >
                                            {entryDates}
                                        </Text>

                                        <DividerNew className="my-3" />

                                        <Text>Application Period Closes</Text>
                                        <Text
                                            size="lg"
                                            fontWeight="semibold"
                                            className="mb-2"
                                        >
                                            {closeTime.format(types.DATE_TIME_FORMAT)}
                                        </Text>
                                        {this.renderProgressBar({
                                            openTime,
                                            closeTime,
                                            valueLabel: `${daysLeft} ${daysLeftLabel} left`,
                                        })}

                                        <DividerNew className="my-3" />

                                        <Text>Lottery Results Available</Text>
                                        <Text size="lg" fontWeight="semibold">
                                            {announcedTime.format(types.DATE_FORMAT)}{' '}
                                            (estimated)
                                        </Text>
                                    </div>

                                    <DividerNew className="my-3" />

                                    <div className="rec-form-item-wrap-full">
                                        <Button
                                            id="enter-lottery-button"
                                            onClick={() =>
                                                this.enterLottery(
                                                    types.RES_ID_DELIMITER +
                                                        this.props.permit.id,
                                                    lotteryId
                                                )
                                            }
                                            shouldFitContainer
                                            className="mb-2"
                                            type="button"
                                        >
                                            Register for Lottery
                                        </Button>
                                        {walkMeThroughButton}
                                    </div>
                                    <div className="rec-form-item-wrap-full">
                                        {this.renderLotteryInfoLink()}
                                        {isDailyLottery &&
                                            permit.id === types.HALF_DOME_ID && (
                                                <Button
                                                    to={`/permits/${permit.id}/lotteryschedule`}
                                                    appearance="link"
                                                    id="lottery-schedule-btn"
                                                    shouldFitContainer
                                                    screenReaderTextBefore="View"
                                                    iconBeforeElement={
                                                        <Icon iconName="calendar-note" />
                                                    }
                                                >
                                                    Lottery Schedule
                                                </Button>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })
            .filter((item) => item);
    }

    renderLotteryInfoLink = () => {
        const { flags } = this.props;
        return flags?.enableLotteryTransparency ? (
            <Button appearance="link" shouldFitContainer href="/lottery/available">
                Lottery Statistics and Information
            </Button>
        ) : (
            <Button appearance="link" shouldFitContainer href="/lottery/how-they-work">
                How Does the Lottery Work?
            </Button>
        );
    };

    renderDisplayedLotteryComponent() {
        const { lotteries = [], currentDate, permit } = this.props;
        return lotteries
            .map((lottery) => {
                const {
                    id: lotteryId,
                    status,
                    display_at,
                    open_at,
                    close_at,
                    announced_at,
                    inventory_info,
                } = lottery;
                const displayTime = gnoment(display_at).tz(permit.time_zone);
                const openTime = gnoment(open_at).tz(permit.time_zone);
                const closeTime = gnoment(close_at).tz(permit.time_zone);
                const announcedTime = gnoment(announced_at).tz(permit.time_zone);
                const lotteryRangeStart = gnoment(get(inventory_info, 'dates.start')).tz(
                    permit.time_zone
                );
                const lotteryRangeEnd = gnoment(get(inventory_info, 'dates.end')).tz(
                    permit.time_zone
                );

                if (status !== 'LotteryStatusActive') {
                    return null;
                }

                if (
                    !displayTime.isValid() ||
                    !openTime.isValid() ||
                    !closeTime.isValid() ||
                    !lotteryRangeStart.isValid() ||
                    !lotteryRangeEnd.isValid() ||
                    !announcedTime.isValid()
                ) {
                    return null;
                }

                if (!currentDate.isBetween(displayTime, openTime)) {
                    return null;
                }

                if (!hasAccessToLottery(SecurityHelper.getAccount(), permit)) {
                    return null;
                }

                const isDailyLottery = this.isDailyLottery(lottery.id);
                const entryDates = lotteryRangeStart.isSame(lotteryRangeEnd)
                    ? lotteryRangeStart.format(types.DATE_FORMAT)
                    : `${lotteryRangeStart.format(types.DATE_FORMAT)} - ${lotteryRangeEnd.format(types.DATE_FORMAT)}`;

                return (
                    <div key={lotteryId}>
                        <div className="rec-aside-header">
                            <div>{`Future ${isDailyLottery ? 'Daily ' : ''}Lottery`}</div>
                        </div>
                        <div className="rec-aside-body">
                            <div className="right-rail-card">
                                <div className="rec-form-item-wrapper">
                                    <div>
                                        <p>
                                            This lottery can give you a reservation for
                                            this permit during the following dates:
                                        </p>
                                        <p>
                                            <b>{entryDates}</b>
                                        </p>
                                    </div>

                                    <FlexRow>
                                        <FlexCol>
                                            <p>Application Opens</p>
                                            <p>
                                                <b>
                                                    {openTime.format(types.DATE_FORMAT)}
                                                </b>
                                            </p>
                                        </FlexCol>
                                        <FlexCol>
                                            <p>Results Available</p>
                                            <p>
                                                <b>
                                                    {announcedTime.format(
                                                        types.DATE_FORMAT
                                                    )}
                                                </b>
                                            </p>
                                        </FlexCol>
                                    </FlexRow>
                                    <div>
                                        <Divider />
                                        {this.renderLotteryInfoLink()}
                                        {isDailyLottery &&
                                            permit.id === types.HALF_DOME_ID && (
                                                <Button
                                                    to={`/permits/${permit.id}/lotteryschedule`}
                                                    appearance="link"
                                                    id="lottery-schedule-btn"
                                                    shouldFitContainer
                                                    screenReaderTextBefore="View"
                                                    iconBeforeElement={
                                                        <Icon iconName="calendar-note" />
                                                    }
                                                >
                                                    Lottery Schedule
                                                </Button>
                                            )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })
            .filter((item) => item);
    }

    renderUnauthorizedComponent() {
        return (
            <div>
                <div className="rec-aside-header rec-aside-unauth">
                    <div>Not Authorized</div>
                </div>
                <div className="rec-aside-body">
                    <div className="right-rail-card">
                        <p>
                            You are not authorized to make commercial reservations at this
                            location, please contact the facility for more information.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    renderUnauthorizedLotteryComponent() {
        return (
            <div>
                <div className="rec-aside-header per-unauth-header">
                    <div>Lottery Unauthorized</div>
                </div>
                <div className="rec-aside-body">
                    <div className="right-rail-card">
                        <p>
                            You are not authorized to register for a lottery at this
                            location, please contact the facility for more information.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    renderAvailability() {
        if (!hasAccessToPermit(SecurityHelper.getAccount(), this.props.permit.id)) {
            return this.renderUnauthorizedComponent();
        }

        const openLotteryComponent = this.renderOpenLotteryComponent();
        const availabilityComponent = this.renderAvailabilityComponent();
        const displayedLotteryComponent = this.renderDisplayedLotteryComponent();

        if (
            isEmpty(openLotteryComponent) &&
            isEmpty(availabilityComponent) &&
            isEmpty(displayedLotteryComponent)
        ) {
            return this.renderRegistrationIsClosed();
        }

        return (
            <>
                {this.renderOpenLotteryComponent()}
                {this.renderAvailabilityComponent()}
                {this.renderDisplayedLotteryComponent()}
                <RecaptchaChallengeModal
                    isOpen={this.state.isChallengeModalOpen}
                    onClose={() => this.setState({ isChallengeModalOpen: false })}
                    onContinue={() =>
                        this.navigateToPermitRegistration(this.pendingStartDate)
                    }
                    recaptcha={this.props.recaptcha}
                    isSubmitting={this.state.isFetching}
                    action={recaptchaConstants.CH_PERMIT_VENUE_BOOKING}
                />
            </>
        );
    }

    render() {
        if (!this.props.permit.rules) {
            return (
                <div>
                    <div className="rec-aside-header">
                        <div>Loading...</div>
                    </div>
                </div>
            );
        }
        return this.renderAvailability();
    }
}

function select(state) {
    return {
        permit: state.permit.result,
        issuance: state.permit.issuance,
        checkIn: state.search.checkin_time,
        availability: state.permit.availability,
        availabilities: state.permit.availabilities,
        account: state.auth.account,
        issuanceError: state.permit.issuanceError,
        aggSeasonRules: state.permit.agg_season_rules,
        lotteries: state.permit.lotteries,
    };
}

const mapActionsToProps = (dispatch) => ({
    pActions: bindActionCreators(pActions, dispatch),
});

export default withCompatRouter(
    withLDConsumer()(
        connect(select, mapActionsToProps)(withRecaptchaFallback(PermitAvailability))
    )
);
